import { useState } from "react";
import axios from "axios";
import { Lock, BarChart3, Users, Eye, TrendingUp, Loader2 } from "lucide-react";
import { useSEO } from "@/lib/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-2xl border border-brand-ink/10 bg-white p-6" data-testid={testId}>
    <div className="flex items-center gap-3 text-brand-primary">
      <Icon className="h-5 w-5" />
      <span className="text-xs font-semibold tracking-[0.15em] uppercase text-brand-ink/50">{label}</span>
    </div>
    <div className="mt-4 font-display font-black text-4xl md:text-5xl gradient-text leading-none">{value}</div>
  </div>
);

export default function Analytics() {
  useSEO({ title: "Analytics | Tangensis", description: "Private analytics dashboard.", path: "/analytics" });
  const [key, setKey] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/analytics/summary`, { params: { key } });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.status === 401 ? "Incorrect password." : "Something went wrong. Try again.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const maxDay = data ? Math.max(1, ...data.byDay.map((d) => d.views)) : 1;
  const maxPage = data ? Math.max(1, ...data.topPages.map((p) => p.views)) : 1;

  return (
    <div className="min-h-screen bg-brand-light pt-32 pb-24" data-testid="analytics-page">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12">
        <h1 className="font-heading font-extrabold text-4xl md:text-5xl gradient-text tracking-tight">Analytics</h1>
        <p className="mt-3 text-brand-ink/60">Private dashboard — traffic and interest across your site.</p>

        {!data && (
          <form onSubmit={load} className="mt-10 max-w-md" data-testid="analytics-login">
            <label className="text-sm font-semibold text-brand-ink/70">Dashboard password</label>
            <div className="mt-3 flex gap-3">
              <div className="relative flex-1">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-ink/40" />
                <input
                  type="password"
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  placeholder="Enter password"
                  data-testid="analytics-password-input"
                  className="w-full rounded-full border border-brand-ink/15 bg-white pl-11 pr-4 py-3 outline-none focus:border-brand-primary transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !key}
                data-testid="analytics-submit"
                className="rounded-full bg-brand-primary text-white px-6 py-3 font-semibold hover:bg-brand-deepmagenta transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "View"}
              </button>
            </div>
            {error && <p className="mt-3 text-sm text-red-600" data-testid="analytics-error">{error}</p>}
          </form>
        )}

        {data && (
          <div className="mt-10 space-y-10" data-testid="analytics-dashboard">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Eye} label="Total views" value={data.total} testId="stat-total" />
              <StatCard icon={Users} label="Unique visitors" value={data.unique} testId="stat-unique" />
              <StatCard icon={TrendingUp} label="Last 7 days" value={data.last7} testId="stat-last7" />
              <StatCard icon={BarChart3} label="Last 30 days" value={data.last30} testId="stat-last30" />
            </div>

            <div className="rounded-2xl border border-brand-ink/10 bg-white p-6">
              <h2 className="font-heading font-bold text-xl text-brand-ink">Views — last 14 days</h2>
              <div className="mt-6 flex items-end gap-2 h-40" data-testid="analytics-byday">
                {data.byDay.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-2" title={`${d.date}: ${d.views}`}>
                    <div className="w-full rounded-t-md gradient-bg" style={{ height: `${(d.views / maxDay) * 100}%`, minHeight: d.views ? "4px" : "0" }} />
                    <span className="text-[10px] text-brand-ink/40">{d.date.slice(5)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-brand-ink/10 bg-white p-6">
              <h2 className="font-heading font-bold text-xl text-brand-ink">Top pages</h2>
              <div className="mt-6 space-y-3" data-testid="analytics-toppages">
                {data.topPages.map((p) => (
                  <div key={p.path} className="flex items-center gap-4">
                    <span className="w-48 shrink-0 text-sm text-brand-ink/80 truncate">{p.label}</span>
                    <div className="flex-1 h-3 rounded-full bg-brand-light overflow-hidden">
                      <div className="h-full gradient-bg" style={{ width: `${(p.views / maxPage) * 100}%` }} />
                    </div>
                    <span className="w-12 text-right text-sm font-semibold tabular-nums text-brand-ink">{p.views}</span>
                  </div>
                ))}
              </div>
            </div>

            {data.projectDetail.length > 0 && (
              <div className="rounded-2xl border border-brand-ink/10 bg-white p-6">
                <h2 className="font-heading font-bold text-xl text-brand-ink">Most-viewed projects</h2>
                <div className="mt-6 space-y-3" data-testid="analytics-projects">
                  {data.projectDetail.map((p) => (
                    <div key={p.label} className="flex items-center justify-between border-b border-brand-ink/5 pb-2">
                      <span className="text-sm text-brand-ink/80">{p.label}</span>
                      <span className="text-sm font-semibold tabular-nums text-brand-primary">{p.views}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
